// src/stores/annual-returns-store.ts
import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import { isAxiosError } from 'axios';
import { api } from 'boot/axios';
import {
  downloadBlobResponse,
  getResponseData,
  notifyError,
  readBlobErrorMessage,
} from 'utils';
import { trace } from 'boot/trace';
import { usePagination } from 'src/composables';
import {
  AR_STATUS_OPTIONS,
  createEmptyStatusCounts,
  type ArStatusCounts,
} from 'src/constants/annualReturnStatus';
import type { AnnualReturnType } from 'src/constants/annualReturnType';
import type {
  AnnualReturnInvoice,
  AnnualReturnInvoiceEligibility,
} from 'src/types/annualReturnInvoice';
import type { AnnualReturnAdjustmentRow } from 'stores/annual-return-adjustments-store';
import { usePermissionsStore } from 'stores/permissions-store';
import { useAnnualReturnsVisibility } from 'src/composables/annual-returns/useAnnualReturnsVisibility';

type AnnualReturnOrganisation = {
  id: number | null;
  name?: string | null;
  description?: string | null;
};

type AnnualReturnRegion = {
  id: number | null;
  name?: string | null;
  short_name?: string | null;
  region_type_short_description?: string | null;
};

export type AnnualReturnDispensation = {
  id: number;
  description?: string | null;
  dispensation_reason?: string | null;
  status_date?: string | null;
  required_by_date?: string | null;
  active_from?: string | null;
  active_to?: string | null;
  nunc_pro_tunc_flag?: boolean;
  fee_waived_flag?: boolean;
  dispensation_type?: {
    id: number | null;
    description?: string | null;
    short_description?: string | null;
  } | null;
  // The awarding body's foreign keys: a dispensation is granted by a region
  // (`region_id`) or centrally by the organisation (`region_id` null). Resolve
  // the display names from the client-side dictionaries by these ids.
  organisation_id?: number | null;
  region_id?: number | null;
  unit_id?: number | null;
  annual_return_amount?: number | string | null;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
};

/**
 * One payment recorded against a return, in the shape both payment arrays carry.
 * Read-only: manual payments are written through their own store, and online
 * payments arrive from the finance provider via a reconcile.
 */
export type AnnualReturnPaymentRow = {
  id: number;
  description: string | null;
  amount: number | string;
  reference: string | null;
  notes: string | null;
  /** `Y-m-d H:i:s`, not ISO-8601. */
  received_at: string | null;
};

export type AnnualReturnRow = {
  id: number;
  unit_id?: number | null;
  /** `Y-m-d`; the date the unit went live on Hermes, null if it has not. */
  unit_live_on_hermes_at?: string | null;
  type?: AnnualReturnType | string | null;
  type_label?: string | null;
  organisation_id?: number | null;
  region_id?: number | null;
  ar_year?: number | null;
  annual_return_rule_snapshot_id?: number | null;
  rule_snapshot_key?: string | null;
  rule_snapshot_rules_count?: number | null;
  status?: string | null;
  status_label?: string | null;
  members_total?: number | null;
  missing_members?: unknown[] | null;
  has_missing_members?: boolean | null;
  missing_members_count?: number | null;
  member_fees_total?: number | string | null;
  per_ar_total?: number | string | null;
  manual_adjustments_total?: number | string | null;
  manual_payments_total?: number | string | null;
  online_payments_total?: number | string | null;
  // Which of the two is populated follows the return's type: organisation
  // returns are paid online through the provider, regional returns by hand.
  manual_payments?: AnnualReturnPaymentRow[] | null;
  online_payments?: AnnualReturnPaymentRow[] | null;
  dispensations_total?: number | string | null;
  active_from?: string | null;
  active_to?: string | null;
  due_at?: string | null;
  secretary_accepted_at?: string | null;
  treasurer_accepted_at?: string | null;
  issued_at?: string | null;
  agreed_at?: string | null;
  locked_at?: string | null;
  total_owed?: number | string | null;
  total_payments_received?: number | string | null;
  outstanding_balance?: number | string | null;
  is_mutable?: boolean;
  is_negotiable?: boolean;
  is_locked?: boolean;
  is_settled?: boolean;
  accepts_adjustments?: boolean;
  accepts_payments?: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  deleted_at?: string | null;
  organisation?: AnnualReturnOrganisation | null;
  region?: AnnualReturnRegion | null;
  summary?: unknown[] | null;
  dispensations?: AnnualReturnDispensation[] | null;
  adjustments?: AnnualReturnAdjustmentRow[] | null;
  invoice?: AnnualReturnInvoice | null;
  invoice_eligibility?: AnnualReturnInvoiceEligibility | null;
};

type StoredFilters = {
  sortBy?: string | null;
  permissionContext?: 'organisation' | 'region';
  contextFilter?: 'organisation' | 'region';
  organisationId?: number | null;
  regionTypeId?: number | null;
  regionId?: number | null;
  unitId?: number | null;
  search?: string;
  status?: string;
  arYear?: number | null;
  activeFrom?: string | null;
  activeTo?: string | null;
  recycleBin?: string;
  hasMissingMembers?: 'all' | 'yes' | 'no';
  paymentStatus?: AnnualReturnPaymentStatus;
};

/**
 * How much of what a return owes has been received. Derived server-side from
 * `total_owed` against `total_payments_received` at query time, so there is no
 * column to sort on and no facet count per value.
 *
 * The empty string is "any status", and is omitted from the query rather than
 * sent blank — the API validates the value against the other three.
 */
export type AnnualReturnPaymentStatus = '' | 'unpaid' | 'part_paid' | 'paid';

const STORAGE_KEY = 'annualReturnsManagement:filters';

export const ANNUAL_RETURN_OPTIONS = {
  sortOptions: [
    { label: 'Active From (Newest)', value: '-active_from' },
    { label: 'Active From (Oldest)', value: 'active_from' },
    { label: 'Created At (Newest)', value: '-created_at' },
    { label: 'Created At (Oldest)', value: 'created_at' },
  ],
  statusOptions: [...AR_STATUS_OPTIONS],
  missingMembersOptions: [
    { label: 'All returns', value: 'all' },
    { label: 'With missing members', value: 'yes' },
    { label: 'Without missing members', value: 'no' },
  ],
  // "Any" rather than "All": a return owing nothing — fee-exempt, or credited
  // below zero — sits in none of the three states and drops out of the list
  // whenever one is chosen, so these do not partition the returns in view.
  paymentStatusOptions: [
    { label: 'Any payment status', value: '' },
    { label: 'Unpaid', value: 'unpaid' },
    { label: 'Part paid', value: 'part_paid' },
    { label: 'Paid', value: 'paid' },
  ],
};

export const useAnnualReturnsStore = defineStore('annual-returns', () => {
  const {
    items,
    loading,
    pagination,
    hasMoreData,
    loadingMore,
    buildQueryParams,
    updatePagination,
    updateSort: baseUpdateSort,
    resetPagination,
    loadMoreData,
    onVirtualScroll: baseOnVirtualScroll,
  } = usePagination<AnnualReturnRow>('-active_from', 36);

  // ── Permission context ──────────────────────────────────────────────
  const permissionsStore = usePermissionsStore();

  const canManageOrganisation = computed(() =>
    permissionsStore.can({ can: 'ANNUAL_RETURNS:ALLOW:MANAGE_ORGANISATION' }),
  );

  const canManageRegion = computed(() =>
    permissionsStore.can({ can: 'ANNUAL_RETURNS:ALLOW:MANAGE_REGION' }),
  );

  const canManageUnit = computed(() =>
    permissionsStore.can({ can: 'ANNUAL_RETURNS:ALLOW:MANAGE_UNIT' }),
  );

  const canToggleContext = computed(
    () => canManageOrganisation.value && canManageRegion.value,
  );

  const isUnitOnlyContext = computed(
    () =>
      canManageUnit.value &&
      !canManageOrganisation.value &&
      !canManageRegion.value,
  );

  // Entitlement alone is not enough to put a region or unit in front of the
  // user: it also has to be live on the Portal. The visibility composable takes
  // that intersection, so these lists are already narrowed to what the
  // interface may offer.
  const {
    visibleRegionIds: permittedRegionIds,
    visibleUnitIds: permittedUnitIds,
  } = useAnnualReturnsVisibility();

  function resolvePermissionContext(): 'organisation' | 'region' | 'unit' {
    if (canManageOrganisation.value) return 'organisation';
    if (canManageRegion.value) return 'region';
    if (canManageUnit.value) return 'unit';
    return 'organisation';
  }

  // ── Stored filters ──────────────────────────────────────────────────
  function loadFiltersFromStorage(): StoredFilters {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return {};
      return JSON.parse(raw) as StoredFilters;
    } catch {
      return {};
    }
  }

  const stored = loadFiltersFromStorage();

  const contextFilter = ref<'organisation' | 'region'>(
    stored.contextFilter || 'organisation',
  );

  const organisationId = ref<number | null>(
    typeof stored.organisationId === 'number' ? stored.organisationId : null,
  );
  const regionTypeId = ref<number | null>(
    typeof stored.regionTypeId === 'number' ? stored.regionTypeId : null,
  );
  const regionId = ref<number | null>(
    typeof stored.regionId === 'number' ? stored.regionId : null,
  );
  const unitId = ref<number | null>(
    typeof stored.unitId === 'number' ? stored.unitId : null,
  );

  const search = ref<string>(stored.search || '');
  const status = ref<string>(stored.status || '');
  const arYear = ref<number | null>(
    typeof stored.arYear === 'number' ? stored.arYear : null,
  );
  const activeFrom = ref<string | null>(stored.activeFrom || null);
  const activeTo = ref<string | null>(stored.activeTo || null);
  const recycleBin = ref<string>(stored.recycleBin || 'hide');
  const hasMissingMembers = ref<'all' | 'yes' | 'no'>(
    stored.hasMissingMembers || 'all',
  );
  const paymentStatus = ref<AnnualReturnPaymentStatus>(
    stored.paymentStatus || '',
  );

  // Honour the persisted permission context when the user is allowed to toggle
  // and the stored value is one they can manage; otherwise fall back to the
  // permission-derived default.
  function resolveInitialPermissionContext():
    'organisation' | 'region' | 'unit' {
    const canToggle = canManageOrganisation.value && canManageRegion.value;
    if (
      canToggle &&
      (stored.permissionContext === 'organisation' ||
        stored.permissionContext === 'region')
    ) {
      return stored.permissionContext;
    }
    return resolvePermissionContext();
  }

  const permissionContext = ref<'organisation' | 'region' | 'unit'>(
    resolveInitialPermissionContext(),
  );

  // Ensure contextFilter from localStorage is valid for the user's permissions
  if (!canManageOrganisation.value && canManageRegion.value) {
    contextFilter.value = 'region';
  }
  if (canManageOrganisation.value && !canManageRegion.value) {
    contextFilter.value = 'organisation';
  }
  if (isUnitOnlyContext.value) {
    contextFilter.value = 'organisation';
  }

  // The permission bundle may not be loaded when the store is first
  // instantiated, in which case the initial resolution above defaults to
  // 'organisation'. Re-derive the context once the permissions become
  // available: a user who cannot toggle (single-permission) is always pinned to
  // their derived context, so a region-only admin correctly lands in 'region'.
  // Users who can toggle keep their chosen/persisted context.
  watch(
    [canManageOrganisation, canManageRegion, canManageUnit],
    () => {
      if (canToggleContext.value) return;
      permissionContext.value = resolvePermissionContext();
    },
    { immediate: true },
  );

  if (stored.sortBy) {
    pagination.value.sortBy = stored.sortBy;
  }

  // ── Status summary counts (from API meta.status_counts) ─────────────
  // Independent of the selected status filter; reflects all OTHER active
  // filters. Refreshed only on fetchData (page 1), never on next-page loads.
  const statusCounts = ref<ArStatusCounts>(createEmptyStatusCounts());

  const statusCountsAll = computed(() =>
    Object.values(statusCounts.value).reduce((sum, count) => sum + count, 0),
  );

  const viewVersion = ref<any>(null);

  function setViewVersion(version: any | null) {
    viewVersion.value = version;
  }

  function setPermissionContext(context: 'organisation' | 'region') {
    if (!canToggleContext.value) return;
    permissionContext.value = context;
  }

  // Reset all cached filter state on logout: removes the persisted filters from
  // localStorage and returns the in-memory refs to their defaults so a
  // subsequent login never inherits the previous user's filters.
  function clearCachedFilters() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    contextFilter.value = 'organisation';
    permissionContext.value = 'organisation';
    organisationId.value = null;
    regionTypeId.value = null;
    regionId.value = null;
    unitId.value = null;
    search.value = '';
    status.value = '';
    arYear.value = null;
    activeFrom.value = null;
    activeTo.value = null;
    recycleBin.value = 'hide';
    hasMissingMembers.value = 'all';
    paymentStatus.value = '';
    statusCounts.value = createEmptyStatusCounts();
    items.value = [];
    resetPagination();
  }

  function saveFiltersToStorage() {
    const toStore: StoredFilters = {
      sortBy: pagination.value.sortBy,
      // Only the toggleable contexts are persisted; the 'unit' context is a
      // runtime-only state (unit-only users are pinned and never store it).
      permissionContext:
        permissionContext.value === 'unit'
          ? 'organisation'
          : permissionContext.value,
      contextFilter: contextFilter.value,
      organisationId: organisationId.value,
      regionTypeId: regionTypeId.value,
      regionId: regionId.value,
      unitId: unitId.value,
      search: search.value,
      status: status.value,
      arYear: arYear.value,
      activeFrom: activeFrom.value,
      activeTo: activeTo.value,
      recycleBin: recycleBin.value,
      hasMissingMembers: hasMissingMembers.value,
      paymentStatus: paymentStatus.value,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
    } catch {}
  }

  // Assemble the active filter values shared by the list and the export, so the
  // two never drift apart. `context` is a top-level param (not under `filter[]`)
  // and is returned separately by the callers that need it.
  function collectFilters(): Record<string, any> {
    const f: Record<string, any> = {};

    if (organisationId.value != null) f.organisation_id = organisationId.value;
    // The region type is a real back-end filter in the organisation context
    // only (in the region context it merely narrows the Region dropdown and is
    // never sent). It narrows to its regions when no specific region is chosen;
    // a selected region is the tighter constraint and supersedes it.
    if (
      permissionContext.value === 'organisation' &&
      regionId.value == null &&
      regionTypeId.value != null
    )
      f.region_type_id = regionTypeId.value;
    if (regionId.value != null) f.region_id = regionId.value;
    if (unitId.value != null) f.unit_id = unitId.value;
    if (search.value) f.search = search.value;
    if (status.value) f.status = status.value;
    if (arYear.value != null) f.ar_year = arYear.value;
    if (activeFrom.value) f.active_from = activeFrom.value;
    if (activeTo.value) f.active_to = activeTo.value;
    if (recycleBin.value && recycleBin.value !== 'hide')
      f.recycle_bin = recycleBin.value;
    if (hasMissingMembers.value === 'yes') f.has_missing_members = 1;
    if (hasMissingMembers.value === 'no') f.has_missing_members = 0;
    // Omitted entirely for "any"; an empty string is refused as a 422.
    if (paymentStatus.value) f.payment_status = paymentStatus.value;

    return f;
  }

  function buildQueryString(): string {
    const f = collectFilters();
    f.context = contextFilter.value;

    const queryParams = buildQueryParams(f);
    return queryParams ? `${queryParams}&oc=1` : 'oc=1';
  }

  const hasActiveFilters = computed(() => {
    return !!(
      search.value ||
      status.value ||
      arYear.value != null ||
      activeFrom.value ||
      activeTo.value ||
      (recycleBin.value && recycleBin.value !== 'hide') ||
      hasMissingMembers.value !== 'all' ||
      !!paymentStatus.value
    );
  });

  // Status is controlled by the summary cards, not the filter drawer, so it
  // must not drive the "Clear all filters" button — only non-status filters do.
  const hasNonStatusFilters = computed(() => {
    return !!(
      search.value ||
      arYear.value != null ||
      activeFrom.value ||
      activeTo.value ||
      (recycleBin.value && recycleBin.value !== 'hide') ||
      hasMissingMembers.value !== 'all' ||
      !!paymentStatus.value
    );
  });

  function clearFilter(filterType: string) {
    switch (filterType) {
      case 'search':
        search.value = '';
        break;
      case 'status':
        status.value = '';
        break;
      case 'arYear':
        arYear.value = null;
        break;
      case 'activeFrom':
        activeFrom.value = null;
        break;
      case 'activeTo':
        activeTo.value = null;
        break;
      case 'recycleBin':
        recycleBin.value = 'hide';
        break;
      case 'hasMissingMembers':
        hasMissingMembers.value = 'all';
        break;
      case 'paymentStatus':
        paymentStatus.value = '';
        break;
      case 'all':
        search.value = '';
        status.value = '';
        arYear.value = null;
        activeFrom.value = null;
        activeTo.value = null;
        recycleBin.value = 'hide';
        hasMissingMembers.value = 'all';
        paymentStatus.value = '';
        break;
      default:
        break;
    }

    saveFiltersToStorage();
    pagination.value.page = 1;
    void fetchData();
  }

  async function fetchData() {
    loading.value = true;
    try {
      const queryString = buildQueryString();
      const response = await api.get(`/api/v1/annual-returns?${queryString}`);

      items.value = (getResponseData(response) as AnnualReturnRow[]) ?? [];

      if (response.data?.meta?.page) {
        updatePagination(response.data.meta.page);
      }

      const counts = response.data?.meta?.status_counts;
      statusCounts.value = counts
        ? { ...createEmptyStatusCounts(), ...counts }
        : createEmptyStatusCounts();
    } catch (error: any) {
      trace('Error fetching annual returns', 'error', error);
      items.value = [];
      if (error?.status === 422) throw error;
    } finally {
      loading.value = false;
    }
  }

  async function fetchNextPageData(): Promise<AnnualReturnRow[]> {
    if (pagination.value.page >= pagination.value.lastPage) return [];

    pagination.value.page += 1;
    const queryString = buildQueryString();
    const response = await api.get(`/api/v1/annual-returns?${queryString}`);

    const newItems = (getResponseData(response) as AnnualReturnRow[]) ?? [];
    items.value = [...items.value, ...newItems];

    if (response.data?.meta?.page) {
      updatePagination(response.data.meta.page);
    }

    return newItems;
  }

  async function handleLoadMore(): Promise<void> {
    await loadMoreData(fetchNextPageData);
  }

  // ── Export ──────────────────────────────────────────────────────────
  // Stream the whole filtered set (scope=all) as a CSV/XLSX download. Reuses
  // `collectFilters` + the current sort so the export always matches the list;
  // pagination is irrelevant to scope=all, and `oc` is a list-cache flag the
  // export endpoint does not use. The response is a file blob, not JSON, so it
  // bypasses the usual `getResponseData` unwrapping and is handed to the browser
  // as a download. Errors are surfaced and re-thrown so the caller can clear its
  // busy state.
  async function exportAnnualReturns(format: 'csv' | 'xlsx'): Promise<void> {
    const filters = collectFilters();
    filters.context = contextFilter.value;

    const params = new URLSearchParams();
    params.set('scope', 'all');
    params.set('format', format);
    params.set('sort', pagination.value.sortBy);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.set(
          `filter[${key}]`,
          Array.isArray(value) ? value.join(',') : String(value),
        );
      }
    });

    try {
      const response = await api.get(
        `/api/v1/annual-returns/export?${params.toString()}`,
        { responseType: 'blob' },
      );

      downloadBlobResponse(response, `annual-returns-all.${format}`);
    } catch (error: any) {
      // Surface the API's own reason (e.g. a 422 validation message) where the
      // Blob error body carries one.
      const message =
        (await readBlobErrorMessage(error?.response?.data)) ??
        'The annual returns export could not be generated.';
      // A 403/401 already raises its own toast in the interceptor; avoid a
      // duplicate for those statuses.
      const status = error?.response?.status;
      if (status !== 401 && status !== 403) notifyError(message);
      trace('Error exporting annual returns', 'error', error);
      throw error;
    }
  }

  /**
   * Download one annual return as a PDF (its member list followed by the
   * breakdown of charges). The API renders it on every request, so it always
   * reflects the return's current data. The response is the file itself, not
   * JSON, so it is handed straight to the browser as a download named by the
   * API's `Content-Disposition` header.
   *
   * `Accept: application/json` makes failures come back as JSON (inside a
   * Blob). The generic error toast is suppressed so we can word the 404 and 500
   * cases ourselves; the interceptor still handles 401 (logout) and 403 (no
   * access) with its own toasts. Errors are re-thrown so the caller can clear
   * its busy state and react to the status.
   */
  async function downloadAnnualReturnPdf(
    annualReturnId: number,
  ): Promise<void> {
    try {
      const response = await api.get<Blob>(
        `/api/v1/annual-returns/${annualReturnId}/pdf`,
        {
          responseType: 'blob',
          headers: { Accept: 'application/json' },
          suppressErrorNotify: true,
        },
      );
      downloadBlobResponse(response, `annual-return-${annualReturnId}.pdf`);
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      if (status === 404) {
        notifyError('This annual return is no longer available.');
      } else if (status !== 401 && status !== 403) {
        notifyError('Could not generate the PDF, please try again.');
      }
      trace('Error downloading annual return PDF', 'error', error);
      throw error;
    }
  }

  function onVirtualScroll(details: { to: number }) {
    baseOnVirtualScroll(details, handleLoadMore);
  }

  function updateSort(value: string) {
    const result = baseUpdateSort(value, fetchData);
    saveFiltersToStorage();
    return result;
  }

  // ── Unit-facing "my returns" ────────────────────────────────────────
  // The unit dashboard (/annual-returns/my-returns) shows the signed-in
  // officer's own returns, grouped by unit. It reuses the index endpoint
  // filtered by unit_id (CSV) — the server applies ACL scope and, for
  // unit-level officers, returns BOTH organisation and regional ARs. This
  // state is independent of the admin index above (its own list + loading),
  // so the two views never clobber one another.
  const myReturns = ref<AnnualReturnRow[]>([]);
  const myReturnsLoading = ref(false);
  const myReturnsSelectedUnitId = ref<number>(0);

  /**
   * Fetch the annual returns for the given units in a single request. Used by
   * the unit dashboard; does not touch the admin index list, filters or
   * pagination. Returns the rows and stores them in `myReturns`.
   */
  async function fetchMyReturns(unitIds: number[]): Promise<AnnualReturnRow[]> {
    if (!unitIds.length) {
      myReturns.value = [];
      return [];
    }

    myReturnsLoading.value = true;
    try {
      // The unit IDs originate from the permission bundle (raw API JSON), so at
      // runtime they may arrive as strings despite the `number[]` type. Coerce
      // to integers so the `filter[unit_id]` CSV is always integer-only.
      const integerUnitIds = unitIds
        .map((unitId) => Number.parseInt(String(unitId), 10))
        .filter((unitId) => Number.isInteger(unitId));

      if (!integerUnitIds.length) {
        myReturns.value = [];
        return [];
      }

      const params = new URLSearchParams();
      params.set('filter[unit_id]', integerUnitIds.join(','));
      params.set('oc', '1');
      const response = await api.get(
        `/api/v1/annual-returns?${params.toString()}`,
      );
      myReturns.value = (getResponseData(response) as AnnualReturnRow[]) ?? [];
      return myReturns.value;
    } catch (error) {
      trace('Error fetching unit annual returns', 'error', error);
      myReturns.value = [];
      return [];
    } finally {
      myReturnsLoading.value = false;
    }
  }

  // ── Single annual return (unit-facing detail page) ──────────────────
  // The unit return page (/annual-returns/units, route `unit-return`) shows one
  // return in full. It fetches by id via the apiResource `show`, which returns
  // the complete AnnualReturnResource — including the embedded `summary` (fee
  // lines), `dispensations` and `missing_members`. Kept in its own state so it
  // never clobbers the admin index list or the dashboard's `myReturns`.
  const currentReturn = ref<AnnualReturnRow | null>(null);
  const currentReturnLoading = ref(false);

  /**
   * Whether the current return's unit is live on Hermes. Driven by
   * `unit_live_on_hermes_at`: a date means live, null (or absent) means not on
   * Hermes.
   */
  const isOnHermes = computed(
    () => currentReturn.value?.unit_live_on_hermes_at != null,
  );

  /**
   * Fetch a single annual return by id, storing it in `currentReturn`. Passes
   * `oc=1` to bypass the per-user cache so the detail page always reflects the
   * latest figures after a write. Returns the row, or null on failure.
   */
  async function fetchAnnualReturn(
    annualReturnId: number,
  ): Promise<AnnualReturnRow | null> {
    currentReturnLoading.value = true;
    try {
      const response = await api.get(
        `/api/v1/annual-returns/${annualReturnId}?oc=1`,
      );
      const row = (getResponseData(response) as AnnualReturnRow) ?? null;
      currentReturn.value = row;
      return row;
    } catch (error) {
      trace('Error fetching annual return', 'error', error);
      currentReturn.value = null;
      return null;
    } finally {
      currentReturnLoading.value = false;
    }
  }

  /**
   * Replace the record-only `missing_members` list on an annual return. The PUT
   * swaps the whole list (the endpoint validates each entry against the AR's
   * inbound movement types and period). Refreshes `currentReturn` so the detail
   * page reflects the saved list. Returns the updated row, or null on failure.
   */
  async function updateMissingMembers(
    annualReturnId: number,
    missingMembers: unknown[],
  ): Promise<AnnualReturnRow | null> {
    await api.put(`/api/v1/annual-returns/${annualReturnId}/missing-members`, {
      missing_members: missingMembers,
    });
    return fetchAnnualReturn(annualReturnId);
  }

  async function deleteAnnualReturn(annualReturnId: number) {
    try {
      await api.delete(`/api/v1/annual-returns/${annualReturnId}`);
      resetPagination();
      await fetchData();
    } catch (error) {
      trace('deleteAnnualReturn failed', 'error', error);
      throw error;
    }
  }

  async function restoreAnnualReturn(annualReturnId: number) {
    try {
      await api.patch(`/api/v1/annual-returns/${annualReturnId}/restore`);
      resetPagination();
      await fetchData();
    } catch (error) {
      trace('restoreAnnualReturn failed', 'error', error);
      throw error;
    }
  }

  async function acceptAnnualReturn(
    annualReturnId: number,
    role: 'secretary' | 'treasurer',
  ) {
    const response = await api.patch(
      `/api/v1/annual-returns/${annualReturnId}/accept`,
      { annual_return_id: annualReturnId, role },
    );
    return response;
  }

  async function updateAnnualReturn(
    id: number,
    payload: Partial<{
      unit_id: number;
      type: AnnualReturnType;
      ar_year: number;
    }>,
  ) {
    try {
      const response = await api.put(`/api/v1/annual-returns/${id}`, payload);
      const updated = getResponseData(response);
      resetPagination();
      await fetchData();
      return updated;
    } catch (e) {
      trace('updateAnnualReturn failed', 'error', e);
      throw e;
    }
  }

  // Invoice reads and the four invoice commands live in
  // stores/annual-return-invoice-store.ts. The row type keeps its `invoice` and
  // `invoice_eligibility` fields, which that store primes itself from.

  return {
    items,
    annualReturns: items,
    loading,
    loadingMore,
    pagination,
    hasMoreData,

    contextFilter,
    organisationId,
    regionTypeId,
    regionId,
    unitId,
    search,
    status,
    arYear,
    activeFrom,
    activeTo,
    recycleBin,
    hasMissingMembers,
    paymentStatus,

    hasActiveFilters,
    hasNonStatusFilters,
    statusCounts,
    statusCountsAll,

    permissionContext,
    canManageOrganisation,
    canManageRegion,
    canManageUnit,
    canToggleContext,
    isUnitOnlyContext,
    permittedRegionIds,
    permittedUnitIds,

    fetchData,
    fetchNextPageData,
    exportAnnualReturns,
    downloadAnnualReturnPdf,
    onVirtualScroll,
    resetPagination,
    updateSort,
    saveFiltersToStorage,
    clearFilter,
    clearCachedFilters,

    myReturns,
    myReturnsLoading,
    myReturnsSelectedUnitId,
    fetchMyReturns,

    currentReturn,
    currentReturnLoading,
    isOnHermes,
    fetchAnnualReturn,
    updateMissingMembers,

    updateAnnualReturn,
    acceptAnnualReturn,
    deleteAnnualReturn,
    restoreAnnualReturn,

    viewVersion,
    setViewVersion,

    setPermissionContext,
  };
});
